import classNames from 'classnames';
import React, { memo, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import JSCookie from 'js-cookie';
import { Skeleton } from '@sravni/react-design-system';
import { nanoid } from 'nanoid';
import { useBoolean, usePrevious, preventBodyScroll } from '@sravni/react-utils';
import Queue from '@sravni/utils/lib/queue';
import { parseUTMCookie } from '@sravni/utils/lib/analytics';
import _isEqual from 'lodash/isEqual';
import ReactDOM from 'react-dom';
import pluralize from '@sravni/utils/lib/pluralize';

var OutOfPageTypes;
(function (OutOfPageTypes) {
    OutOfPageTypes["FULLSCREEN"] = "FullScreen";
    OutOfPageTypes["MOBILE_BOTTOM"] = "MobileBottom";
})(OutOfPageTypes || (OutOfPageTypes = {}));
var BannerFromIframeToBannerType = {
    'showFullscreen': OutOfPageTypes.FULLSCREEN,
    'fullSreenBannerHTML5': OutOfPageTypes.FULLSCREEN,
    'mobileBanner': OutOfPageTypes.MOBILE_BOTTOM,
};
var BANNER_TYPES;
(function (BANNER_TYPES) {
    BANNER_TYPES["TOP"] = "top";
    BANNER_TYPES["SIDE"] = "side";
    BANNER_TYPES["TGBM_7"] = "tgbm7";
    BANNER_TYPES["OUT_OF_PAGE"] = "outOfPage";
    BANNER_TYPES["TGB_BOTTOM"] = "tgbBottom";
})(BANNER_TYPES || (BANNER_TYPES = {}));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var modules_7cb51497 = {"banner":"_gx1j0z"};
/*SRAVNI__src/src/components/Banner/banner.module.scss__SRAVNI*/
console.warn("Can't replace 'SRAVNI__src/src/components/Banner/banner.module.scss__SRAVNI' in dist/index.esm.js, because asset does not found");

var loadJs = function (src) {
    return new Promise(function (resolve, reject) {
        var tag = document.createElement('script');
        tag.type = 'text/javascript';
        tag.async = false;
        tag.addEventListener('load', resolve);
        tag.addEventListener('error', reject);
        tag.src = src;
        document.body.appendChild(tag);
    });
};

var promiseOnce = function (fn) {
    var promise;
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!promise) {
            promise = Promise.resolve(fn.apply(void 0, __spreadArray([], __read(args))));
        }
        return promise;
    };
};

var queue = new Queue({ debug: false });

var AD_FOX_DICTIONARY = {
    order: 'puid1',
    alias: 'puid2',
    pid: 'puid3',
    ip: 'puid4',
    bi: 'puid5',
    pm: 'puid6 ',
    serveys: 'puid7',
    vs: 'puid8',
    sravniaffid: 'puid9',
    bic: 'puid10',
    teg: 'puid11',
    rubrika: 'puid12',
    page_type: 'puid13',
    subStatus: 'puid14',
    auth: 'puid15',
    // 16???
    utmcsr: 'puid17',
    utmcmd: 'puid18',
    ab_test: 'puid19',
    organizationType: 'puid20',
};
var YANDEX = 'yandex';
var COMPANY_ID_YANDEX = 302581;

var BANNER_TYPES_WITH_PLACEHOLDERS = [BANNER_TYPES.TGBM_7, BANNER_TYPES.SIDE];

var modules_efc4e723 = {"container":"_17zrx2n","placeholder":"_3i5240","placeholder_side":"_16dfsvq","placeholder_tgbm7":"_map2y8"};
/*SRAVNI__src/src/components/AdFox/styles.module.scss__SRAVNI*/
console.warn("Can't replace 'SRAVNI__src/src/components/AdFox/styles.module.scss__SRAVNI' in dist/index.esm.js, because asset does not found");

var loadYandexAdsContextOnce = promiseOnce(function () { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, loadJs('https://yandex.ru/ads/system/context.js')];
            case 1: return [2 /*return*/, _a.sent()];
        }
    });
}); });
var initBanner = function (_a) {
    var lazy = _a.lazy, containerId = _a.containerId, decoratedParams = _a.decoratedParams, onRender = _a.onRender, onError = _a.onError;
    window.yaContextCb = window.yaContextCb || [];
    window.yaContextCb.push(function () {
        // @ts-expect-error
        Ya.adfoxCode.create({
            lazyLoad: lazy,
            ownerId: COMPANY_ID_YANDEX,
            containerId: containerId,
            params: decoratedParams,
            onRender: onRender,
            onError: onError,
        });
    });
};
// Yandex Banner
var AdFox$1 = memo(function (_a) {
    var banner = _a.banner, className = _a.className, _b = _a.lazy, lazy = _b === void 0 ? false : _b, abName = _a.abName, type = _a.type, onRender = _a.onRender;
    var _c = __read(useState(), 2), containerId = _c[0], setContainerId = _c[1];
    var _d = __read(useState(), 2), isBannerLoaded = _d[0], setBannerLoaded = _d[1];
    var _e = __read(useBoolean(), 2), isError = _e[0], setError = _e[1];
    var prevBanner = usePrevious(banner);
    var decoratedParams = useMemo(function () {
        var _a, _b, _c;
        if (!banner || !banner.params) {
            return {};
        }
        var tag = banner.tag, params = banner.params;
        var mainParams = Object.entries(params).reduce(function (acc, _a) {
            var _b, _c;
            var _d = __read(_a, 2), key = _d[0], value = _d[1];
            if (!value)
                return acc;
            var normalizedValue = Array.isArray(value) ? value.join(':') : value;
            if (!normalizedValue)
                return acc;
            var tagFromAdFoxDictionary = AD_FOX_DICTIONARY[key];
            if (!tagFromAdFoxDictionary) {
                return __assign(__assign({}, acc), (_b = {}, _b[key] = normalizedValue, _b));
            }
            return __assign(__assign({}, acc), (_c = {}, _c[tagFromAdFoxDictionary] = normalizedValue, _c));
        }, {});
        var utmzCookie = JSCookie.get('__utmz');
        var _d = parseUTMCookie(utmzCookie !== null && utmzCookie !== void 0 ? utmzCookie : ''), utmcmd = _d.utmcmd, utmcsr = _d.utmcsr;
        var abCookie = abName && JSCookie.get(abName);
        return __assign(__assign(__assign(__assign({ slotTag: tag }, mainParams), (utmcsr && (_a = {}, _a[AD_FOX_DICTIONARY.utmcsr] = utmcsr, _a))), (utmcmd && (_b = {}, _b[AD_FOX_DICTIONARY.utmcmd] = utmcmd, _b))), (abCookie && (_c = {}, _c[AD_FOX_DICTIONARY.ab_test] = abName + "_" + abCookie, _c)));
    }, [abName, banner]);
    useEffect(function () {
        if (_isEqual(banner, prevBanner))
            return;
        var id = nanoid();
        setContainerId(id);
        setBannerLoaded(false);
    }, [banner, prevBanner, setBannerLoaded]);
    useEffect(function () {
        if (!containerId || isBannerLoaded || !banner)
            return;
        setBannerLoaded(true);
        queue.push(function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, loadYandexAdsContextOnce().catch(setError.on)];
                    case 1:
                        _a.sent();
                        initBanner({
                            lazy: lazy,
                            containerId: containerId,
                            decoratedParams: decoratedParams,
                            onRender: onRender,
                            onError: setError.on,
                        });
                        return [2 /*return*/];
                }
            });
        }); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerId, isBannerLoaded, banner]);
    var withPlaceholder = BANNER_TYPES_WITH_PLACEHOLDERS.includes((banner === null || banner === void 0 ? void 0 : banner.bannerType) || type);
    return (React.createElement("div", { id: containerId, key: containerId, className: classNames(className, modules_efc4e723.container) }, withPlaceholder && !isError && (React.createElement(Skeleton.Button, { className: classNames(modules_efc4e723.placeholder, 'adfox_placeholder', modules_efc4e723["placeholder_" + ((banner === null || banner === void 0 ? void 0 : banner.bannerType) || type)]) }))));
});

// Гугл реклама отключена в данный момент
var AdBanner = memo(function (_a) {
    var _b = _a.loadWhenInView, loadWhenInView = _b === void 0 ? false : _b, props = __rest(_a, ["loadWhenInView"]);
    if (props.banner && props.banner.source !== YANDEX) {
        return null;
    }
    return React.createElement(AdFox$1, __assign({ lazy: loadWhenInView }, props));
});

var modules_7e6c94c2 = {"pixelImg":"_136tz4c","pixels":"_1sqhn4g"};
/*SRAVNI__src/src/components/Pixel/pixel.module.scss__SRAVNI*/
console.warn("Can't replace 'SRAVNI__src/src/components/Pixel/pixel.module.scss__SRAVNI' in dist/index.esm.js, because asset does not found");

var Pixel$1 = memo(function (_a) {
    var pixels = _a.pixels;
    if (!pixels)
        return null;
    var links = pixels.split(',');
    if (!links)
        return null;
    return (React.createElement("div", { className: modules_7e6c94c2.pixels }, links.map(function (url) { return React.createElement("img", { src: url, className: modules_7e6c94c2.pixelImg, alt: '', key: "pixel-link-" + url }); })));
});

var generateHolder = function (_a) {
    var _b = _a.urlHolderTag, urlHolderTag = _b === void 0 ? 'a' : _b, children = _a.children, props = __rest(_a, ["urlHolderTag", "children"]);
    return React.createElement(urlHolderTag, props, children);
};
var MobileBottomWrapper = memo(function (_a) {
    var bannerInfo = _a.bannerInfo, setBannerShown = _a.setBannerShown;
    var _b = __read(useBoolean(true), 2), isBannerVisible = _b[0], setBannerVisible = _b[1];
    var _c = __read(useBoolean(true), 2), isCloseButtonVisible = _c[0], setCloseButtonVisible = _c[1];
    var handleClickCloseButton = useCallback(function (e) {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        setBannerVisible.off();
        setBannerShown();
    }, [setBannerShown, setBannerVisible]);
    var _d = bannerInfo, pixels = _d.pixels, containerClass = _d.containerClass, isNeedCloseButton = _d.needCloseBtn, styles = _d.styles, url = _d.url, urlHolderClass = _d.urlHolderClass, urlHolderTag = _d.urlHolderTag, timer = _d.timer, label = _d.label, labelClass = _d.labelClass;
    useEffect(function () {
        var timerId = setTimeout(function () {
            setCloseButtonVisible.on();
        }, timer !== null && timer !== void 0 ? timer : 0);
        return function () {
            clearTimeout(timerId);
        };
    }, [setCloseButtonVisible, timer]);
    var HolderElement = useMemo(function () { return generateHolder({
        urlHolderTag: urlHolderTag,
        children: React.createElement(React.Fragment, null,
            isNeedCloseButton && isCloseButtonVisible && React.createElement("div", { className: 'btn-close', onClick: handleClickCloseButton }),
            React.createElement("div", { className: labelClass }, label)),
        className: urlHolderClass,
        href: url,
        target: '_blank',
        rel: 'noreferrer',
    }); }, [urlHolderTag, isNeedCloseButton, isCloseButtonVisible, handleClickCloseButton, labelClass, label, urlHolderClass, url]);
    return (React.createElement("div", { className: containerClass, "data-js": 'mobileBottomBanner' }, pixels === null || pixels === void 0 ? void 0 :
        pixels.map(function (pixel) { return React.createElement(Pixel$1, { pixels: pixel, key: "pixel-" + pixel + "-" + nanoid() }); }),
        React.createElement("style", null, styles),
        isBannerVisible && HolderElement));
});

var modules_3734c084 = {"wrapper":"_hm0t4t","wrapper--hidden":"_lie1db","overlay":"_1r7zugn","innerWrapper":"_mxvxqd","header":"_1y1hoxf","closeButton":"_k2xnka"};
import "./assets/index.esm-be36db5d.css";

var Cross = function () { return (React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("g", { clipPath: "url(#clip0)" },
        React.createElement("path", { d: "M10 9.99998L5.05029 14.9497M5.05029 5.05023L10 9.99998L5.05029 5.05023ZM10 9.99998L14.9497 14.9497L10 9.99998ZM10 9.99998L14.9497 5.05023L10 9.99998Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
        React.createElement("path", { d: "M79 -22H-51C-53.7614 -22 -56 -19.7614 -56 -17V37C-56 39.7614 -53.7614 42 -51 42H79C81.7614 42 84 39.7614 84 37V-17C84 -19.7614 81.7614 -22 79 -22Z", stroke: "currentColor", strokeDasharray: "10 5" })),
    React.createElement("defs", null,
        React.createElement("clipPath", { id: "clip0" },
            React.createElement("rect", { width: "20", height: "20", fill: "white" }))))); };

var portalContainer;
var portalAttribute = 'portal-banner-container';
var FullScreenWrapper$1 = memo(function (_a) {
    var _b;
    var bannerInfo = _a.bannerInfo, setBannerShown = _a.setBannerShown;
    var _c = __read(useBoolean(false), 2), isContainerMounted = _c[0], setIsContainerMounted = _c[1];
    var _d = __read(useBoolean(false), 2), isBannerVisible = _d[0], setBannerVisible = _d[1];
    var firstRenderRef = useRef(true);
    var _e = __read(useState(10), 2), countdownTime = _e[0], setCountdownTime = _e[1];
    var _f = bannerInfo, pixels = _f.pixels, _g = _f.data, width = _g.width, height = _g.height, scripts = _g.content2, contentStyle = _g.contentStyle, html = _g.content3;
    var portal = React.useMemo(function () {
        if (portalContainer) {
            return portalContainer;
        }
        var el = document.querySelector("div[" + portalAttribute + "]");
        if (el) {
            portalContainer = el;
            return portalContainer;
        }
        portalContainer = document.createElement('div');
        portalContainer.setAttribute(portalAttribute, '');
        return portalContainer;
    }, []);
    var handleCloseModal = useCallback(function () {
        setBannerVisible.off();
    }, [setBannerVisible]);
    useEffect(function () {
        var _a, _b;
        if (!firstRenderRef.current)
            return;
        setBannerShown();
        setBannerVisible.on();
        setCountdownTime((_b = +((_a = bannerInfo === null || bannerInfo === void 0 ? void 0 : bannerInfo.data) === null || _a === void 0 ? void 0 : _a.timer)) !== null && _b !== void 0 ? _b : 10);
        firstRenderRef.current = false;
    }, [(_b = bannerInfo === null || bannerInfo === void 0 ? void 0 : bannerInfo.data) === null || _b === void 0 ? void 0 : _b.timer, setBannerShown, setBannerVisible]);
    useEffect(function () {
        if (!isBannerVisible)
            return;
        var counterId = setInterval(function () {
            if (countdownTime > 0) {
                setCountdownTime(countdownTime - 1);
            }
            else if (countdownTime === 0) {
                handleCloseModal();
            }
        }, 1000);
        return function () {
            clearInterval(counterId);
        };
    }, [countdownTime, handleCloseModal, isBannerVisible]);
    useEffect(function () {
        if (!portal || isContainerMounted)
            return;
        document.body.appendChild(portal);
        setIsContainerMounted.on();
    }, [isContainerMounted, portal, setIsContainerMounted]);
    useEffect(function () {
        if (isBannerVisible) {
            preventBodyScroll(true);
        }
        else {
            preventBodyScroll(false);
        }
        return function () {
            return preventBodyScroll();
        };
    }, [isBannerVisible]);
    var banner = useMemo(function () {
        var _a;
        return (React.createElement("div", { className: classNames(modules_3734c084.wrapper, (_a = {}, _a[modules_3734c084['wrapper--hidden']] = !isBannerVisible, _a)) },
            React.createElement("div", { className: modules_3734c084.overlay, onClick: handleCloseModal }),
            React.createElement("div", { className: modules_3734c084.innerWrapper },
                React.createElement(Pixel$1, { pixels: isBannerVisible ? pixels === null || pixels === void 0 ? void 0 : pixels.onShow : pixels === null || pixels === void 0 ? void 0 : pixels.onClose }),
                React.createElement("div", { className: modules_3734c084.header },
                    React.createElement("div", null,
                        "\u0411\u0430\u043D\u043D\u0435\u0440 \u0437\u0430\u043A\u0440\u043E\u0435\u0442\u0441\u044F \u0447\u0435\u0440\u0435\u0437 ",
                        countdownTime,
                        " ",
                        pluralize(countdownTime, 'секунду', 'секунды', 'секунд')),
                    React.createElement("div", { className: modules_3734c084.closeButton, onClick: handleCloseModal },
                        React.createElement(Cross, null))),
                React.createElement("script", null, scripts),
                React.createElement("style", null, contentStyle),
                React.createElement("div", { style: { width: +width + "px", height: +height + "px", overflow: 'hidden' }, dangerouslySetInnerHTML: { __html: html } }))));
    }, [contentStyle, countdownTime, handleCloseModal, height, html, isBannerVisible, pixels === null || pixels === void 0 ? void 0 : pixels.onClose, pixels === null || pixels === void 0 ? void 0 : pixels.onShow, scripts, width]);
    if (portal && isContainerMounted) {
        return ReactDOM.createPortal(banner, portal);
    }
    return null;
});

var _a;
var outOfPageWrappersMap = (_a = {},
    _a[OutOfPageTypes.MOBILE_BOTTOM] = MobileBottomWrapper,
    _a[OutOfPageTypes.FULLSCREEN] = FullScreenWrapper$1,
    _a);
var useOutOfPageBanner = function (isOutOfPage) {
    var _a = __read(useBoolean(false), 2), wasBannerShown = _a[0], setBannerShown = _a[1];
    var _b = __read(useState({}), 2), bannerInfo = _b[0], setBannerInfo = _b[1];
    var _c = __read(useState(), 2), bannerType = _c[0], setBannerType = _c[1];
    var messageListener = useCallback(function (_a) {
        var dataFromMessage = _a.data;
        if (!dataFromMessage || wasBannerShown) {
            return;
        }
        var data;
        try {
            data = typeof dataFromMessage === 'string' ? JSON.parse(dataFromMessage) : dataFromMessage;
        }
        catch (_) {
            data = dataFromMessage;
        }
        if (Object.keys(BannerFromIframeToBannerType).includes(data.type)) {
            setBannerType(BannerFromIframeToBannerType[data.type]);
            setBannerInfo(data.branding);
        }
    }, [wasBannerShown]);
    useEffect(function () {
        if (!isOutOfPage)
            return;
        window.addEventListener('message', messageListener);
        return function () {
            window.removeEventListener('message', messageListener);
        };
    }, [isOutOfPage, messageListener]);
    var WrapperType = useMemo(function () {
        if (!bannerType)
            return null;
        return outOfPageWrappersMap[bannerType];
    }, [bannerType]);
    var props = useMemo(function () { return ({
        bannerInfo: bannerInfo,
        setBannerShown: setBannerShown.on,
    }); }, [bannerInfo, setBannerShown.on]);
    if (!Object.values(bannerInfo).length)
        return null;
    if (!WrapperType)
        return null;
    return {
        Wrapper: WrapperType,
        props: props,
    };
};

var Banner$1 = memo(function (_a) {
    var lazy = _a.lazy, className = _a.className, type = _a.type, adProps = __rest(_a, ["lazy", "className", "type"]);
    var wrapper = useOutOfPageBanner(type === 'outOfPage');
    var bannerElement = React.createElement(AdBanner, __assign({ className: classNames(className, modules_7cb51497.banner), loadWhenInView: lazy, type: type }, adProps));
    if (!wrapper)
        return bannerElement;
    var Wrapper = wrapper.Wrapper, props = wrapper.props;
    return (React.createElement(React.Fragment, null,
        bannerElement,
        React.createElement(Wrapper, __assign({}, props, { className: className }))));
});

export { BANNER_TYPES, Banner$1 as Banner };
